import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Arwes, Loading } from "arwes"
import styled, { css, keyframes } from "styled-components"

import { MarginTop } from "../../common/styled/MarginTop"
import { ContentColumn, PageOffset } from "../../common/styled/Page"
import UserAvatar from "../../common/UserAvatar"
import { formatAgoCompact } from "../../common/util/dates"
import { fetchCells, joinCell, leaveCell } from "./redux/reducers"

// Cells Directory — implements the "Cells Directory" design: red CELLS
// banner, TRENDING · 7D sparklines, then the roster split into MY CELLS
// (joined, green) and DISCOVER (purple). Rows expand into a "live tap"
// preview of the cell's last transmissions.

const TONES = {
  joined: {
    frame: "rgba(23, 255, 14, 0.5)",
    corner: "rgba(23, 255, 14, 0.9)",
    bg: "linear-gradient(180deg, rgba(0, 30, 5, 0.4), rgba(0, 18, 3, 0.22))",
    divider: "rgba(23, 255, 14, 0.25)"
  },
  discover: {
    frame: "rgba(190, 38, 252, 0.5)",
    corner: "rgba(190, 38, 252, 0.9)",
    bg: "linear-gradient(180deg, rgba(14, 6, 44, 0.55), rgba(8, 4, 28, 0.35))",
    divider: "rgba(190, 38, 252, 0.25)"
  }
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: clamp(14px, 3vw, 30px) clamp(12px, 3.5vw, 38px);
  font-family: "Rajdhani", sans-serif;
  color: #d6e1ff;
`

// Four bracket corners for the banner / trending panels (the design's
// signature frame). Parent must be position: relative.
const Corner = styled.div`
  position: absolute;
  width: 14px;
  height: 14px;
  pointer-events: none;
`

const CornerBrackets = ({ color }) => (
  <>
    <Corner
      style={{ top: -2, left: -2, borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}` }}
    />
    <Corner
      style={{ top: -2, right: -2, borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}` }}
    />
    <Corner
      style={{ bottom: -2, left: -2, borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}` }}
    />
    <Corner
      style={{ bottom: -2, right: -2, borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}` }}
    />
  </>
)

const Banner = styled.div`
  position: relative;
  border: 1px solid rgba(255, 0, 28, 0.7);
  background: linear-gradient(180deg, rgba(42, 0, 6, 0.8), rgba(22, 0, 3, 0.55));
  box-shadow: 0 0 20px rgba(255, 0, 28, 0.16), inset 0 0 26px rgba(255, 0, 28, 0.08);
  padding: 15px 22px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 10px 18px;
  flex-wrap: wrap;
`

const BannerTitle = styled.div`
  font-family: "Rajdhani", sans-serif;
  font-weight: 700;
  font-size: 30px;
  letter-spacing: 7px;
  line-height: 1;
  color: #ff2438;
  text-shadow: 0 0 14px rgba(255, 0, 28, 0.6);
`

const BannerMeta = styled.div`
  font-family: "VT323", monospace;
  font-size: 17px;
  letter-spacing: 2px;
  color: #ffb800;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px 12px;
  flex-wrap: wrap;
`

const Tagline = styled.div`
  flex: 1 1 280px;
  min-width: 0;
  font-family: "VT323", monospace;
  font-size: 16px;
  letter-spacing: 1px;
  color: #00e7ff;
`

const FlashNote = styled.span`
  color: #17ff0e;
`

const BUTTON_TONES = {
  cyan: css`
    background: rgba(0, 231, 255, 0.06);
    border-color: rgba(0, 231, 255, 0.7);
    color: #bff4ff;

    &:hover {
      background: rgba(0, 231, 255, 0.14);
      box-shadow: 0 0 12px rgba(0, 231, 255, 0.35);
    }
  `,
  purple: css`
    background: rgba(190, 38, 252, 0.08);
    border-color: rgba(190, 38, 252, 0.75);
    color: #e2a9ff;

    &:hover {
      background: rgba(190, 38, 252, 0.18);
      box-shadow: 0 0 12px rgba(190, 38, 252, 0.4);
    }
  `,
  // "✓ Joined" — reads green, but hovering it forecasts the disconnect.
  joined: css`
    background: rgba(23, 255, 14, 0.06);
    border-color: rgba(23, 255, 14, 0.6);
    color: #baffb4;

    &:hover {
      background: rgba(255, 0, 28, 0.1);
      border-color: rgba(255, 0, 28, 0.6);
      color: #ffb4bc;
      box-shadow: 0 0 12px rgba(255, 0, 28, 0.25);
    }
  `
}

const NeonButton = styled.button`
  border: 1px solid transparent;
  font-family: "Rajdhani", sans-serif;
  font-weight: 600;
  font-size: ${p => (p.size === "row" ? "13.5px" : "14px")};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: ${p => (p.size === "row" ? "9px 14px" : "10px 16px")};
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, border-color 0.15s, color 0.15s;
  ${p => BUTTON_TONES[p.tone] || BUTTON_TONES.cyan}
`

// Hard on/off flicker — terminal cursor, not a soft fade.
const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0.15; }
`

const Busy = styled.span`
  animation: ${blink} 0.55s linear infinite;
`

// Join/Unjoin share one slot. min-width keeps the frame steady while the
// label cycles Joined → Unjoin (hover) → SEVERING… (in flight).
const MembershipButton = styled(NeonButton)`
  min-width: 118px;

  .swap {
    display: none;
  }

  &:hover:not(:disabled) .idle {
    display: none;
  }

  &:hover:not(:disabled) .swap {
    display: inline;
  }

  &:disabled {
    cursor: wait;
  }
`

// Blinking confirmation strip shown for a couple of seconds after a
// membership change, then removed by a timer.
const NoticeBar = styled.div`
  border: 1px solid
    ${p => (p.kind === "leave" ? "rgba(255, 0, 28, 0.65)" : "rgba(23, 255, 14, 0.55)")};
  background: ${p =>
    p.kind === "leave" ? "rgba(42, 0, 6, 0.6)" : "rgba(0, 30, 5, 0.45)"};
  color: ${p => (p.kind === "leave" ? "#ff9aa4" : "#baffb4")};
  font-family: "VT323", monospace;
  font-size: 15px;
  letter-spacing: 2px;
  padding: 10px 14px;
  text-align: center;
  animation: ${blink} 0.8s linear infinite;
`

const TrendPanel = styled.div`
  position: relative;
  border: 1px solid rgba(190, 38, 252, 0.55);
  background: rgba(8, 4, 40, 0.4);
  padding: 14px 16px 16px;
`

const TrendHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`

const TrendTitle = styled.div`
  font-family: "VT323", monospace;
  font-size: 17px;
  letter-spacing: 3px;
  color: #ffb800;
`

const TrendSub = styled.div`
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
  color: #6d7da6;
`

const TrendGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
  margin-top: 12px;
`

const TrendCard = styled.div`
  position: relative;
  border: 1px solid rgba(190, 38, 252, 0.4);
  background: rgba(8, 4, 40, 0.5);
  padding: 12px 14px;
  display: flex;
  gap: 12px;
  align-items: center;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: rgba(0, 231, 255, 0.85);
    box-shadow: 0 0 12px rgba(0, 231, 255, 0.2);
  }
`

const TrendRank = styled.div`
  font-family: "VT323", monospace;
  font-size: 30px;
  line-height: 1;
  color: rgba(255, 184, 0, 0.4);
`

const TrendBody = styled.div`
  flex: 1;
  min-width: 0;
`

const TrendName = styled.div`
  font-family: "VT323", monospace;
  font-size: 18px;
  color: #00e7ff;
  text-shadow: 0 0 8px rgba(0, 231, 255, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TrendMembers = styled.div`
  font-family: "VT323", monospace;
  font-size: 12.5px;
  letter-spacing: 1px;
  color: #6d7da6;
`

const TrendStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

const TrendBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 24px;
`

const Bar = styled.div`
  width: 5px;
  height: ${p => p.h}px;
  background: linear-gradient(180deg, #17ff0e, rgba(23, 255, 14, 0.2));
  box-shadow: 0 0 4px rgba(23, 255, 14, 0.45);
`

const TrendTx = styled.div`
  font-family: "VT323", monospace;
  font-size: 13px;
  color: #17ff0e;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`

const SectionTitle = styled.div`
  font-family: "VT323", monospace;
  font-size: 18px;
  letter-spacing: 3px;
  color: ${p => p.color};
  text-shadow: 0 0 10px ${p => p.glow};
`

const CountBadge = styled.div`
  font-family: "VT323", monospace;
  font-size: 12.5px;
  letter-spacing: 1px;
  color: ${p => p.color};
  border: 1px solid ${p => p.frame};
  background: ${p => p.fill};
  padding: 2px 8px;
  white-space: nowrap;
`

const DashRule = styled.div`
  flex: 1;
  border-top: 1px dashed ${p => p.color};
`

const EmptyNote = styled.div`
  border: 1px dashed ${p => p.color};
  padding: 18px;
  text-align: center;
  font-family: "VT323", monospace;
  font-size: 15px;
  letter-spacing: 1px;
  color: #6d7da6;
`

const Row = styled.div`
  position: relative;
  cursor: ${p => p.cursor};
  border: 1px solid ${p => TONES[p.tone].frame};
  background: ${p => TONES[p.tone].bg};
  padding: 14px 16px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: rgba(0, 231, 255, 0.9);
    box-shadow: 0 0 16px rgba(0, 231, 255, 0.18);
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    pointer-events: none;
  }

  &::before {
    top: -1px;
    left: -1px;
    border-top: 2px solid ${p => TONES[p.tone].corner};
    border-left: 2px solid ${p => TONES[p.tone].corner};
  }

  &::after {
    bottom: -1px;
    right: -1px;
    border-bottom: 2px solid ${p => TONES[p.tone].corner};
    border-right: 2px solid ${p => TONES[p.tone].corner};
  }
`

const RowMain = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  flex-wrap: wrap;
`

const RowBody = styled.div`
  flex: 1 1 240px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const CellName = styled.span`
  font-family: "VT323", monospace;
  font-size: 22px;
  line-height: 1;
  color: #00e7ff;
  text-shadow: 0 0 8px rgba(0, 231, 255, 0.35);
  letter-spacing: 1px;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const CellDesc = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: rgba(214, 225, 255, 0.85);
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const LastLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  font-family: "VT323", monospace;
  font-size: 14.5px;
  letter-spacing: 0.5px;
  color: #6d7da6;
`

const LastText = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RowActions = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
  align-items: center;
  flex-wrap: wrap;
`

const TapPanel = styled.div`
  margin-top: 12px;
  border-top: 1px dashed ${p => TONES[p.tone].divider};
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TapLabel = styled.div`
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 2.5px;
  color: #ffb800;
`

const TapPost = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`

const TapPostBody = styled.div`
  flex: 1;
  min-width: 0;
`

const TapPostMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-family: "VT323", monospace;
  font-size: 14px;
`

const TapPostText = styled.div`
  border-left: 2px solid #3a4670;
  padding-left: 8px;
  margin-top: 2px;
  font-size: 14.5px;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LoadingWrap = styled.div`
  margin: 30px 0;
  text-align: center;
`

const authorNameOf = post => (post.postedBy && post.postedBy.name) || "unknown"

// Both the row tile (cell creator) and the live-tap authors use the
// app-wide UserAvatar at the same size — no shrinking for nested rows.
const AVATAR_SIZE = 56

class CellsList extends Component {
  state = { expanded: {}, flash: false, pending: {}, notice: null }

  componentDidMount() {
    // Fetch on first visit only — navigating back reuses the store.
    if (!this.props.loaded) {
      this.props.fetchCells()
    }
  }

  componentWillUnmount() {
    clearTimeout(this._flashTimer)
    clearTimeout(this._noticeTimer)
  }

  // Shared join/leave flow: mark the row pending while the request is in
  // flight, then flash a blinking confirmation for a couple of seconds.
  handleMembership = async (cell, action) => {
    this.setState(s => ({ pending: { ...s.pending, [cell._id]: action } }))
    const ok = await (action === "leave"
      ? this.props.leaveCell(cell._id)
      : this.props.joinCell(cell._id))
    this.setState(s => {
      const pending = { ...s.pending }
      delete pending[cell._id]
      return { pending }
    })
    if (ok) {
      clearTimeout(this._noticeTimer)
      this.setState({ notice: { kind: action, name: cell.name } })
      this._noticeTimer = setTimeout(
        () => this.setState({ notice: null }),
        4000
      )
    }
  }

  handleRefresh = async () => {
    await this.props.fetchCells()
    this.setState({ flash: true })
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => this.setState({ flash: false }), 1600)
  }

  toggleExpanded = cellId =>
    this.setState(s => ({
      expanded: { ...s.expanded, [cellId]: !s.expanded[cellId] }
    }))

  renderTrending(cells) {
    const trending = cells
      .filter(c => c.visibility !== "private")
      .map(c => ({ ...c, tx7: (c.tx7d || []).reduce((a, b) => a + b, 0) }))
      .filter(c => c.tx7 > 0)
      .sort((a, b) => b.tx7 - a.tx7)
      .slice(0, 3)
    if (!trending.length) return null
    return (
      <TrendPanel>
        <CornerBrackets color="#be26fc" />
        <TrendHead>
          <TrendTitle>▲ TRENDING · 7D</TrendTitle>
          <TrendSub>post volume across the network · private cells hidden</TrendSub>
        </TrendHead>
        <TrendGrid>
          {trending.map((cell, index) => {
            const max = Math.max(...cell.tx7d, 1)
            return (
              <TrendCard key={cell._id}>
                <TrendRank>0{index + 1}</TrendRank>
                <TrendBody>
                  <TrendName>#{cell.name}</TrendName>
                  <TrendMembers>{cell.memberCount || 0} MEMBERS</TrendMembers>
                </TrendBody>
                <TrendStats>
                  <TrendBars>
                    {cell.tx7d.map((v, i) => (
                      <Bar key={i} h={4 + Math.round((v / max) * 20)} />
                    ))}
                  </TrendBars>
                  <TrendTx>+{cell.tx7} TX</TrendTx>
                </TrendStats>
              </TrendCard>
            )
          })}
        </TrendGrid>
      </TrendPanel>
    )
  }

  renderRow = cell => {
    const joined = !!cell.joined
    const tone = joined ? "joined" : "discover"
    const pending = this.state.pending[cell._id]
    const recent = cell.recentPosts || []
    const last = recent[0]
    const expanded = !!this.state.expanded[cell._id] && recent.length > 0
    return (
      <Row
        key={cell._id}
        tone={tone}
        cursor={recent.length > 0 ? "pointer" : "default"}
        onClick={() => recent.length > 0 && this.toggleExpanded(cell._id)}
      >
        <RowMain>
          <UserAvatar user={cell.createdBy} size={AVATAR_SIZE} square />
          <RowBody>
            <TitleLine>
              <CellName>#{cell.name}</CellName>
              <CountBadge
                color="#ffb800"
                frame="rgba(255, 184, 0, 0.55)"
                fill="rgba(255, 184, 0, 0.06)"
              >
                {cell.txCount || 0} TX
              </CountBadge>
              <CountBadge
                color="#8fa0cc"
                frame="rgba(143, 160, 204, 0.45)"
                fill="rgba(143, 160, 204, 0.06)"
              >
                {cell.memberCount || 0} MEMBERS
              </CountBadge>
            </TitleLine>
            <CellDesc>{cell.description}</CellDesc>
            <LastLine>
              {last ? (
                <>
                  <span style={{ color: "#17ff0e" }}>{expanded ? "▾" : "▸"}</span>
                  <span style={{ color: "#d1ffdb" }}>
                    {formatAgoCompact(last.created)}
                  </span>
                  <span style={{ color: "#00e7ff" }}>{authorNameOf(last)}:</span>
                  <LastText>
                    {last.title} · {last.body}
                  </LastText>
                </>
              ) : (
                <span>NO TRANSMISSIONS YET · this channel is silent</span>
              )}
            </LastLine>
          </RowBody>
          <RowActions onClick={e => e.stopPropagation()}>
            {joined ? (
              <>
                <Link to={`/cell/${cell._id}`} style={{ textDecoration: "none" }}>
                  <NeonButton size="row" tone="cyan">
                    ▸ View
                  </NeonButton>
                </Link>
                <MembershipButton
                  size="row"
                  tone="joined"
                  title="Disconnect from this cell — its feed leaves THE GRID"
                  disabled={!!pending}
                  onClick={() => this.handleMembership(cell, "leave")}
                >
                  {pending === "leave" ? (
                    <Busy>⌁ SEVERING…</Busy>
                  ) : (
                    <>
                      <span className="idle">✓ Joined</span>
                      <span className="swap">✕ Unjoin</span>
                    </>
                  )}
                </MembershipButton>
              </>
            ) : (
              <MembershipButton
                size="row"
                tone="purple"
                disabled={!!pending}
                onClick={() => this.handleMembership(cell, "join")}
              >
                {pending === "join" ? <Busy>⌁ LINKING…</Busy> : "◢ Join"}
              </MembershipButton>
            )}
          </RowActions>
        </RowMain>
        {expanded && (
          <TapPanel tone={tone}>
            <TapLabel>
              ◌ LIVE TAP · LAST {recent.length} TRANSMISSION
              {recent.length > 1 ? "S" : ""}
            </TapLabel>
            {recent.map(post => (
              <TapPost key={post._id}>
                <UserAvatar user={post.postedBy} size={AVATAR_SIZE} square />
                <TapPostBody>
                  <TapPostMeta>
                    <span style={{ color: "#00e7ff" }}>{authorNameOf(post)}</span>
                    <span style={{ color: "#d1ffdb", fontSize: "12.5px" }}>
                      {formatAgoCompact(post.created).toLowerCase()}
                    </span>
                  </TapPostMeta>
                  <TapPostText>
                    <span style={{ color: "#fc26fa" }}>{post.title}</span>
                    <span style={{ color: "#6d7da6" }}> · {post.body}</span>
                  </TapPostText>
                </TapPostBody>
              </TapPost>
            ))}
          </TapPanel>
        )}
      </Row>
    )
  }

  render() {
    const { cells, isExpanded, isLoading } = this.props
    const list = cells || []
    const myCells = list.filter(c => c.joined)
    const discover = list.filter(c => !c.joined)
    const totalTx = list.reduce((sum, c) => sum + (c.txCount || 0), 0)

    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={1200}>
            <Page>
              <Banner>
                <CornerBrackets color="#ff2438" />
                <BannerTitle>CELLS</BannerTitle>
                <BannerMeta>
                  {list.length} CELLS · {totalTx} TRANSMISSIONS
                </BannerMeta>
              </Banner>

              <Toolbar>
                <Tagline>
                  ❯ topic-scoped communities · join a cell to tune it into THE
                  GRID
                  {this.state.flash && <FlashNote> · FEED SYNCED ✓</FlashNote>}
                </Tagline>
                <NeonButton tone="cyan" onClick={this.handleRefresh}>
                  ⟳ Refresh
                </NeonButton>
                <Link to="/createCell" style={{ textDecoration: "none" }}>
                  <NeonButton tone="purple">+ Create Cell</NeonButton>
                </Link>
              </Toolbar>

              {this.state.notice && (
                <NoticeBar kind={this.state.notice.kind}>
                  {this.state.notice.kind === "leave" ? (
                    <>⚠ LINK SEVERED · #{this.state.notice.name} · FEED DETACHED FROM THE GRID</>
                  ) : (
                    <>⌁ LINK ESTABLISHED · #{this.state.notice.name} · FEED ROUTED INTO THE GRID</>
                  )}
                </NoticeBar>
              )}

              {isLoading && (
                <LoadingWrap>
                  <Loading animate small />
                </LoadingWrap>
              )}

              {!isLoading && (
                <>
                  {this.renderTrending(list)}

                  <Section>
                    <SectionHead>
                      <SectionTitle color="#17ff0e" glow="rgba(23, 255, 14, 0.4)">
                        ▣ MY CELLS
                      </SectionTitle>
                      <CountBadge
                        color="#17ff0e"
                        frame="rgba(23, 255, 14, 0.55)"
                        fill="rgba(23, 255, 14, 0.06)"
                      >
                        {myCells.length} LINKED
                      </CountBadge>
                      <DashRule color="rgba(23, 255, 14, 0.25)" />
                    </SectionHead>
                    {myCells.length === 0 ? (
                      <EmptyNote color="rgba(23, 255, 14, 0.35)">
                        NO LINKS ESTABLISHED · join a cell from DISCOVER to
                        route its feed into THE GRID
                      </EmptyNote>
                    ) : (
                      myCells.map(this.renderRow)
                    )}
                  </Section>

                  <Section>
                    <SectionHead>
                      <SectionTitle
                        color="#be26fc"
                        glow="rgba(190, 38, 252, 0.45)"
                      >
                        ◢ DISCOVER
                      </SectionTitle>
                      <CountBadge
                        color="#d987ff"
                        frame="rgba(190, 38, 252, 0.55)"
                        fill="rgba(190, 38, 252, 0.07)"
                      >
                        {discover.length} SIGNALS
                      </CountBadge>
                      <DashRule color="rgba(190, 38, 252, 0.3)" />
                    </SectionHead>
                    {discover.length === 0 ? (
                      <EmptyNote color="rgba(190, 38, 252, 0.4)">
                        {list.length === 0 ? (
                          <>
                            NO SIGNALS ON THE NETWORK · be the first to{" "}
                            <Link to="/createCell">create a cell</Link>
                          </>
                        ) : (
                          "ALL SIGNALS LINKED · you are tuned into every public cell on the network"
                        )}
                      </EmptyNote>
                    ) : (
                      discover.map(this.renderRow)
                    )}
                  </Section>
                </>
              )}
            </Page>
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const mapState = state => ({
  cells: state.cells.list,
  isLoading: state.cells.isLoading,
  loaded: state.cells.loaded
})

export default connect(
  mapState,
  { fetchCells, joinCell, leaveCell }
)(CellsList)
